import { styled } from '@linaria/react';
import type { EdgeProps } from '@xyflow/react';
import { BaseEdge, EdgeLabelRenderer, getStraightPath } from '@xyflow/react';
import { memo, useContext, useMemo } from 'react';
import { Stages, WorkflowStatus } from '../../../api/process-client/process-client.type';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { newCOLORS } from '../../../styles/colors';

import { resetProcessTaskDetails } from '../../process/process-graph/process-graph.helpers';
import { ProcessContext } from '../context/process.context';
import { getAnimatedDotRadius } from '../helpers/edges.helpers';
import type { StageEdgeData } from '../reactflow.type';

const StageEdge = memo(
  ({ data, sourceX, sourceY, targetX, targetY, style = {}, markerEnd }: EdgeProps) => {
    const edgeData = data as StageEdgeData;
    const isRightGraph = edgeData.isRightGraph;
    const processContext = useContext(ProcessContext);
    const processStore = useProcessStore();

    const { setWorkflow: setLeftWorkflow, setRightWorkflow } = processContext;
    const { setWorkflow: setLeftWorkflowStore, setRightWorkflow: setRightWorkflowStore } = processStore;
    const setWorkflow = isRightGraph ? setRightWorkflow : setLeftWorkflow;
    const setWorkflowStore = isRightGraph ? setRightWorkflowStore : setLeftWorkflowStore;

    const [edgePath, labelX, labelY] = useMemo(
      () =>
        getStraightPath({
          sourceX,
          sourceY: sourceY - 5,
          targetX,
          targetY: targetY + 3,
        }),
      [sourceX, sourceY, targetX, targetY],
    );

    // Only render the animated dot if we have a valid path and value
    const showAnimatedDot = edgeData.value > 0 && edgePath;

    // Calculate relative path for animation starting from 0,0
    const relativePath = useMemo(() => {
      if (!edgePath) {
        return '';
      }

      const [endX, endY] = [targetX - sourceX, targetY + 3 - (sourceY - 5)];

      return `M0,0 L${endX},${endY}`;
    }, [sourceX, sourceY, targetX, targetY, edgePath]);

    const handleEdgeClick = () => {
      resetProcessTaskDetails(processContext, processStore);

      const workflow = {
        source: edgeData.sourceStage,
        destination: edgeData.destinationStage,
        status: WorkflowStatus.Expected,
        mapping: edgeData.mapping,
      };

      setWorkflow(workflow);
      setWorkflowStore(workflow);
    };

    return (
      <>
        <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
        {showAnimatedDot && (
          <g transform={`translate(${sourceX},${sourceY - 5})`}>
            <circle r={getAnimatedDotRadius(style.strokeWidth)} fill={style.color}>
              <animateMotion dur="2s" repeatCount="indefinite" path={relativePath} begin={`${Math.random() * 2}s`} />
            </circle>
          </g>
        )}
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(10%, -50%) translate(${labelX + 30}px,${labelY}px)`,
              pointerEvents: 'all',
              cursor: 'pointer',
            }}
            className="nodrag nopan"
            onClick={handleEdgeClick}
          >
            <EdgeLabel data={edgeData} />
          </div>
        </EdgeLabelRenderer>
      </>
    );
  },
  (prev, next) => {
    // Custom comparison to prevent unnecessary rerenders
    const prevData = prev.data as StageEdgeData | undefined;
    const nextData = next.data as StageEdgeData | undefined;
    return (
      prev.sourceX === next.sourceX &&
      prev.sourceY === next.sourceY &&
      prev.targetX === next.targetX &&
      prev.targetY === next.targetY &&
      prevData?.value === nextData?.value &&
      prev.style?.stroke === next.style?.stroke &&
      prev.style?.strokeWidth === next.style?.strokeWidth
    );
  },
);

const EdgeLabel = ({ data }: { data: StageEdgeData }) => {
  const borderColor = data.sourceStage === Stages.Blocked ? newCOLORS.purple2 : newCOLORS.black2;
  const bgColor = data.sourceStage === Stages.Blocked ? newCOLORS.white : newCOLORS.black2;
  const textColor = data.sourceStage === Stages.Blocked ? newCOLORS.purple2 : newCOLORS.white;

  return (
    <Label borderColor={borderColor} bgColor={bgColor} textColor={textColor}>
      <Text>{data.value}</Text>
    </Label>
  );
};

const Label = styled.div<{ borderColor: string; bgColor: string; textColor: string }>`
  min-width: 60px;
  max-width: 120px;
  height: 30px;
  padding: 0 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  border: 1px solid ${({ borderColor }) => borderColor};
  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  white-space: nowrap;
  overflow: hidden;
`;

const Text = styled.div`
  font-family: Figtree;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export { StageEdge };
